import React, { useState, useEffect } from "react";
import { db } from "./firebaseConfig";
import { doc, getDoc } from "firebase/firestore/lite";
import "aos/dist/aos.css";
import "./style.css";
import * as FaIcons from "react-icons/fa";

export const defaultSkills = [
  {
    id: 1,
    category: "Programming Languages",
    iconName: "FaCode",
    color: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300",
    borderColor: "border-blue-300 dark:border-blue-700",
    skills: ["Java", "JavaScript", "TypeScript", "SQL", "PL/SQL"]
  },
  {
    id: 2,
    category: "Frameworks & Libraries",
    iconName: "FaLayerGroup",
    color: "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300",
    borderColor: "border-purple-300 dark:border-purple-700",
    skills: ["Spring Boot", "Spring Security", "Spring AI", "JEE", "ReactJS", "Angular", "Redux"]
  },
  {
    id: 3,
    category: "Backend Technologies",
    iconName: "FaServer",
    color: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300",
    borderColor: "border-green-300 dark:border-green-700",
    skills: ["Microservices", "RESTful API", "Architecture Hexagonale", "Open Feign", "RabbitMQ (AMQP)", "Kafka", "JWT", "OAuth2"]
  },
  {
    id: 4,
    category: "Frontend Technologies",
    iconName: "FaMobile",
    color: "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300",
    borderColor: "border-indigo-300 dark:border-indigo-700",
    skills: ["ReactJS (TSX, JSX)", "Angular", "Redux", "NgRx", "Ext JS", "Tailwind CSS", "PrimeReact", "Bootstrap", "HTML5", "CSS3"]
  },
  {
    id: 5,
    category: "Databases",
    iconName: "FaDatabase",
    color: "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300",
    borderColor: "border-orange-300 dark:border-orange-700",
    skills: ["Oracle", "MySQL", "PostgreSQL", "Firebase", "Redis", "MongoDB"]
  },
  {
    id: 6,
    category: "DevOps & Cloud",
    iconName: "FaCloud",
    color: "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300",
    borderColor: "border-cyan-300 dark:border-cyan-700",
    skills: ["Docker", "Docker Compose", "Jenkins", "GitHub Actions", "AWS EC2", "AWS S3", "OVH", "VPS Management", "Ubuntu", "CI/CD", "Kubernetes"]
  },
  {
    id: 7,
    category: "Security & Authentication",
    iconName: "FaShieldAlt",
    color: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300",
    borderColor: "border-red-300 dark:border-red-700",
    skills: ["Keycloak", "Spring Security", "JWT", "OAuth2", "SSL/TLS"]
  },
  {
    id: 8,
    category: "Development Tools",
    iconName: "FaTools",
    color: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300",
    borderColor: "border-yellow-300 dark:border-yellow-700",
    skills: ["IntelliJ IDEA", "VSCode", "Postman", "Swagger", "Portainer", "ESLint", "Prettier"]
  },
  {
    id: 9,
    category: "Version Control",
    iconName: "FaGitAlt",
    color: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300",
    borderColor: "border-gray-300 dark:border-gray-600",
    skills: ["Git", "GitHub", "GitLab"]
  },
  {
    id: 10,
    category: "Testing & Quality",
    iconName: "FaCheckCircle",
    color: "bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300",
    borderColor: "border-teal-300 dark:border-teal-700",
    skills: ["JUnit", "SonarQube", "Lint", "ESLint", "Postman", "Jest", "Karma", "Mockito"]
  },
  {
    id: 11,
    category: "Project Management",
    iconName: "FaProjectDiagram",
    color: "bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300",
    borderColor: "border-pink-300 dark:border-pink-700",
    skills: ["Jira", "Confluence", "Zoho", "Agile/Scrum", "Kanban"]
  },
  {
    id: 12,
    category: "Modeling & Design",
    iconName: "FaCog",
    color: "bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-300",
    borderColor: "border-slate-300 dark:border-slate-600",
    skills: ["UML", "Merise (MCD)", "Design Patterns", "SOLID Principles", "Clean Architecture", "Architectural Patterns"]
  }
];

const Skills = () => {
  const [skillCategories, setSkillCategories] = useState(defaultSkills);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const docRef = doc(db, 'portfolioData', 'skills');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists() && docSnap.data().items && docSnap.data().items.length > 0) {
          setSkillCategories(docSnap.data().items);
        }
      } catch (error) {
        console.error("Error fetching skills:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSkills();
  }, []);

  if (loading) {
    return (
      <section id="skills" className="bg-white dark:bg-gray-600 pt-16 pb-20 min-h-[40vh] flex items-center justify-center transition-colors duration-300">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primaryColor dark:border-primaryColorDark border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 dark:text-gray-400 font-medium animate-pulse">Loading skills...</p>
        </div>
      </section>
    );
  }

  const renderIcon = (iconName) => {
    const IconComponent = FaIcons[iconName] || FaIcons.FaTools;
    return <IconComponent className="text-xl" />;
  };

  return (
    <section
      id="skills"
      className="bg-white dark:bg-gray-600 pt-16 pb-20 transition-colors duration-300"
    // data-aos="fade-up"
    >
      <div className="container mx-auto px-4">
        <div className="experience-card bg-white dark:bg-gray-700 
          w-full md:w-[900px] lg:w-[1000px] mx-auto mt-10 pb-12 px-4 lg:px-10 
          border-2 border-primaryColor dark:border-primaryColorDark shadow-lg 
          dark:shadow-2xl transition-colors duration-300 rounded-lg">

          {/* Header Section */}
          <div className="text-center mb-12 pt-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 dark:text-primaryColorDark mb-4 transition-colors duration-300">
              <span className="border-b-4 border-primaryColor dark:border-primaryColorDark pb-2 transition-colors duration-300 inline-block">
                Technical Skills
              </span>
            </h1>
            <p className="text-gray-600 dark:text-gray-300 text-lg mt-4 transition-colors duration-300">
              A comprehensive toolkit for building modern, scalable applications
            </p>
          </div>

          {/* Skills Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {skillCategories.map((category, index) => (
              <div
                key={category.id}
                // data-aos="fade-up"
                // data-aos-delay={index * 100}
                className={`bg-white dark:bg-gray-600 rounded-xl p-6 border-2 ${category.borderColor} shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}
              >
                {/* Category Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`${category.color} p-3 rounded-lg`}>
                    {renderIcon(category.iconName)}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white transition-colors duration-300">
                    {category.category}
                  </h3>
                </div>

                {/* Skills List */}
                <div className="flex flex-wrap gap-2">
                  {category.skills.map((skill, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-md hover:bg-primaryColor/10 dark:hover:bg-primaryColorDark/20 hover:text-primaryColor dark:hover:text-primaryColorDark transition-all duration-300 cursor-default"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Summary Section */}
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-500">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-4 bg-primaryColor/10 dark:bg-primaryColorDark/20 rounded-lg">
                <div className="text-3xl font-bold text-primaryColor dark:text-primaryColorDark">4+</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Years Experience</div>
              </div>
              <div className="p-4 bg-primaryColor/10 dark:bg-primaryColorDark/20 rounded-lg">
                <div className="text-3xl font-bold text-primaryColor dark:text-primaryColorDark">50+</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Technologies</div>
              </div>
              <div className="p-4 bg-primaryColor/10 dark:bg-primaryColorDark/20 rounded-lg">
                <div className="text-3xl font-bold text-primaryColor dark:text-primaryColorDark">10+</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Projects Delivered</div>
              </div>
              <div className="p-4 bg-primaryColor/10 dark:bg-primaryColorDark/20 rounded-lg">
                <div className="text-3xl font-bold text-primaryColor dark:text-primaryColorDark">12</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Skill Categories</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
